import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get, isEmpty, noop } from 'lodash-es';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import { VisitorEntities } from 'client/data/models/visitor';
import { PageModel } from 'client/data/models/page';
import {
  buildTrimSummariesPath,
  InventoryCoreConfigurationsModel,
  InventoryTrimSummaryEntities,
} from 'client/data/models/inventory-core-configurations';
import { LEAD_FORMS_CREATIVE_ID } from 'site-modules/shared/components/lead-form/unified-lead-form/constants/creative-ids';
import { CorePageParams } from 'site-modules/shared/utils/core-page/params';
import { isUnknownZip } from 'site-modules/shared/utils/location';
import { getBuildAndPriceLink } from 'site-modules/shared/utils/build-and-price-link-constructor';
import { getSubmodelIdentifier } from 'site-modules/shared/utils/core-page/page-helpers';
import { getLeadFormCta, getTrimsWithLabels } from 'site-modules/shared/utils/core-page/trims-cta-utils';
import { generateNewFlatUrl } from 'site-modules/shared/utils/srp-link-constructor';
import { getLeadFormCreativeId } from 'site-modules/shared/components/lead-form/utils/lead-form-creative-id';
import { getPriceString } from 'site-modules/shared/utils/price-utils';
import { sendPriceCheckerPromoEmail } from 'site-modules/shared/components/lead-form/lead-form-wrapper/lead-form-wrapper-methods';
import { Link } from 'site-modules/shared/components/link/link';
import { TransparentPricingLeadFormDrawer } from 'site-modules/shared/components/lead-form/transparent-pricing-lead-form/transparent-pricing-lead-form-drawer';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';

import { TrimsCtaSimpleSummary } from './trims-cta-simple-summary';
import { TrimDropdownSelector } from './trim-dropdown-selector/trim-dropdown-selector';

import './trims-cta.scss';

const TOP_CORE_UTM_CAMPAIGN = 'core_top_lead_form';

function TrimsCtaUI({
  params,
  vehicle,
  visitorLocation = {},
  showLeadForm = false,
  isDealerlessOem = false,
  isMobile = false,
  trims = null,
  sharedTrimName = null,
  setModelValue,
  pageName = '',
  visitorId = null,
  isCore7026Test = false,
  setDefaultMsrp = noop,
}) {
  const [isLeadFormOpen, toggleLeadFormOpen] = useToggle(false);
  const isPriceDifferenceTracked = useRef(false);
  const currentTrim = (trims && trims.find(({ trimName }) => trimName === sharedTrimName)) || get(trims, '[0]');

  const isInternational = isUnknownZip(visitorLocation);
  const city = get(visitorLocation, 'city', '');
  const zipCode = get(visitorLocation, 'zipCode', '');

  const trackPriceDifference = useCallback(trim => {
    if (isPriceDifferenceTracked.current) {
      return;
    }

    isPriceDifferenceTracked.current = true;

    const totalMsrp = get(trim, 'pricing.totalMsrp', 0);

    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        action_name: TrackingConstant.ACTION_SAVING_RETRIEVAL,
        creative_id: 'edm-entry-model-summary',
        est_savings: totalMsrp - get(trim, 'pricing.suggestedPrice', totalMsrp),
        lf_dealer: trim.hasLeadFormDealers ? 'y' : 'n',
        action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
        action_cause: TrackingConstant.PAGE_LOAD_CAUSE,
      },
    });
  }, []);

  const selectTrim = useCallback(
    newTrim => {
      if (!newTrim) {
        return;
      }

      trackPriceDifference(newTrim);
      setModelValue('sharedValues.trimName', PageModel, newTrim.trimName);
    },
    [setModelValue, trackPriceDifference]
  );

  const trimsKey = trims && trims.map(({ styleId }) => styleId).join();
  useEffect(() => {
    const trim = get(trims, '[0]');
    selectTrim(trim);
    setDefaultMsrp(trim?.pricing?.totalMsrp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimsKey, zipCode]);

  if (isEmpty(trims)) {
    return null;
  }

  const {
    make: { name: makeName, slug: makeSlug },
    model: { name: modelName, slug: modelSlug },
    submodels: { name: submodelName, slug: submodelSlug },
    modelYearId,
    year,
  } = vehicle;

  const trimName = get(currentTrim, 'trimName');
  const vehicleName = `${year} ${makeName} ${modelName}`;

  const {
    pricing: { invoicePrice, totalMsrp, estimatedSavings },
  } = currentTrim;
  const ESP = get(currentTrim, 'pricing.suggestedPrice', totalMsrp);

  const priceCheckerPromoQuery = {
    utm_targetid: visitorId,
    utm_account: 'edmunds_insider',
    utm_campaign: TOP_CORE_UTM_CAMPAIGN,
  };

  const dataTrackingLeadForm = getLeadFormCreativeId({
    isNewVin: true,
    pageNameLegacy: pageName,
    formName: LEAD_FORMS_CREATIVE_ID.TRIM_SUMMARY_FORM,
  });

  let ctaProps = {
    onClick: toggleLeadFormOpen,
    'data-tracking-id': 'view_pricing_details',
    children: getLeadFormCta(isInternational),
  };

  if (!showLeadForm && !isInternational) {
    ctaProps = isDealerlessOem
      ? {
          tag: Link,
          to: getBuildAndPriceLink({
            ...params,
            modelyearId: modelYearId,
            submodel: getSubmodelIdentifier(submodelName, modelName),
            styleId: get(currentTrim, 'styleId'),
          }),
          rel: 'nofollow',
          'data-tracking-id': 'configure_vehicle',
          children: 'Build and Price',
        }
      : {
          tag: Link,
          to: generateNewFlatUrl(params),
          'data-tracking-id': 'trim_summary_srp_link',
          children: 'See All for Sale',
        };
  }

  const explainingText = `${
    invoicePrice
      ? `Knowing the Edmunds estimated invoice price for the ${modelName} ${trimName} is **${getPriceString(
          invoicePrice
        )}** is helpful, but not the whole story.`
      : 'Did you know? You should know exactly what you should pay for a car before you test drive it.'
  }\n\nWe'll show you what to pay on the next page.`;

  const trimsCtaSimpleSummaryProps = {
    className: 'mt-2',
    ctaProps,
    selectedTrim: currentTrim,
    vehicleName,
    vehicleParams: { makeSlug, makeName, modelSlug, modelName, submodelSlug, year },
    showLeadForm: showLeadForm || isInternational,
    isDealerlessOem,
  };

  return (
    <div className="trims-cta">
      <TrimDropdownSelector onChange={selectTrim} selectedTrim={currentTrim} trims={trims} />
      {isCore7026Test ? (
        <Experiment name="core-7026-found" showDefault>
          <Recipe name="ctrl" isDefault>
            <TrimsCtaSimpleSummary {...trimsCtaSimpleSummaryProps} />
          </Recipe>
          <Recipe name="chal">
            <TrimsCtaSimpleSummary {...trimsCtaSimpleSummaryProps} isCore7026Enabled />
          </Recipe>
        </Experiment>
      ) : (
        <TrimsCtaSimpleSummary {...trimsCtaSimpleSummaryProps} />
      )}

      <TransparentPricingLeadFormDrawer
        isOpen={isLeadFormOpen}
        onDrawerToggle={toggleLeadFormOpen}
        params={{
          styleId: get(currentTrim, 'styleId'),
          trim: get(currentTrim, 'trimName'),
          vehicle,
          zipCode,
          city,
        }}
        options={{
          isMobile,
          bestDealPrice: ESP,
          creativeId: dataTrackingLeadForm,
          utmCampaign: TOP_CORE_UTM_CAMPAIGN,
          processDataMethods: {
            postProcessMethods: [sendPriceCheckerPromoEmail],
          },
          priceCheckerParams: { priceCheckerPromoQuery },
          estimatedSavings,
          explainingTextDesktop: explainingText,
          explainingTextMobile: explainingText,
        }}
        creativeId={dataTrackingLeadForm}
      />
    </div>
  );
}

TrimsCtaUI.propTypes = {
  vehicle: VehicleEntities.MakeModelSubmodelYear.isRequired,
  setModelValue: PropTypes.func.isRequired,
  params: CorePageParams.isRequired,
  visitorLocation: VisitorEntities.Location,
  visitorId: PropTypes.string,
  showLeadForm: PropTypes.bool,
  isDealerlessOem: PropTypes.bool,
  isMobile: PropTypes.bool,
  trims: InventoryTrimSummaryEntities.TrimSummaries,
  sharedTrimName: PropTypes.string,
  pageName: PropTypes.string,
  // used in getTrimsWithLabels
  recommendedTrimSlug: PropTypes.string,
  isCore7026Test: PropTypes.bool,
  setDefaultMsrp: PropTypes.func,
};

const mapStateToProps = state => ({
  visitorId: get(state, 'visitor.id'),
  pageName: get(state, 'pageContext.legacy.pageName') || get(state, 'pageContext.page.name'),
});

export const propsAreEqual = (prevProps, { trims }) => !trims;
export const TrimsCta = connect(mapStateToProps)(
  connectToModel(React.memo(TrimsCtaUI, propsAreEqual), {
    sharedTrimName: bindToPath('sharedValues.trimName', PageModel),
    trims: bindToPath(
      ({ params }) => buildTrimSummariesPath(params),
      InventoryCoreConfigurationsModel,
      getTrimsWithLabels
    ),
  })
);

TrimsCta.propTypes = {
  params: CorePageParams.isRequired,
};
